import * as React from 'react';
import { MessageDefinition } from '../../types/message';
import { MESSAGE_KIND, QUESTIONS_TEMPLATE_TYPE } from '../../consts/message';
import moment from 'moment';
import {
  ABSENCE_MESSAGE_INTERVAL_SERVER_TO_CLIENT_MAPPING,
  ABSENCE_MESSAGE_REASON_SERVER_TO_CLIENT_MAPPING
} from '../../consts/absenceMessage';
import {
  AbsenceMessage,
  ClubParticipantInviteMessage,
  EventInvitationMessage,
  EventParticipationMessage,
  TournamentConfirmationRequestMessage
} from '../../models/message';
import { DATE_FORMAT } from '../../consts/date';
import { convertBooleanToYesNo } from 'Src/helpers/accessor/accessor';

interface Props {
  message: MessageDefinition;
}

interface Row {
  title: string;
  field: string;
}

const AbsenceMessageRows: Row[] = [
  { title: 'Date absence', field: 'dateAbsence' },
  { title: 'Interval absence', field: 'intervalAbsence' },
  { title: 'Reason absence', field: 'reasonAbsence' },
  { title: 'Details', field: 'details' },
  { title: 'School comment', field: 'authorizedComment' }
];

const EventParticipationMessageRows: Row[] = [
  { title: 'Take part', field: 'isTakePart' },
  { title: 'Details', field: 'details' }
];

function getValueConverted(type: string, value: any): any {
  switch (type) {
    case QUESTIONS_TEMPLATE_TYPE.NUMBER:
    case QUESTIONS_TEMPLATE_TYPE.STRING:
    case QUESTIONS_TEMPLATE_TYPE.ENUM:
      return value;
    case QUESTIONS_TEMPLATE_TYPE.BOOLEAN:
      return convertBooleanToYesNo(value);
    default:
      return value;
  }
}

const renderFieldTypeLabel = (type: string) => {
  switch (type) {
    case QUESTIONS_TEMPLATE_TYPE.STRING:
      return 'Text';
    case QUESTIONS_TEMPLATE_TYPE.NUMBER:
      return 'Number';
    case QUESTIONS_TEMPLATE_TYPE.BOOLEAN:
      return 'Yes / No';
    case QUESTIONS_TEMPLATE_TYPE.ENUM:
      return 'Options';
    default:
      return String(type || 'Question');
  }
};

function renderQuestionCards(fields: any[]): React.ReactNode {
  if (!Array.isArray(fields) || fields.length === 0) {
    return (
      <div
        style={{
          padding: '12px 14px',
          border: '1px solid rgba(0,0,0,0.07)',
          borderRadius: 12,
          background: '#fff',
          color: '#6b7280',
          fontSize: 14
        }}
      >
        No additional answers recorded.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {fields.map((field, index) => {
        const heading = String(field?.heading || 'Question');
        const type = String(field?.type || '');
        const value = getValueConverted(type, field?.value);
        const isRequired = !!field?.isRequired;
        const enumOptions = Array.isArray(field?.enumOptions) ? field.enumOptions : [];

        return (
          <div
            key={`message-additional-field-${index}`}
            style={{
              padding: '12px 14px',
              border: '1px solid rgba(0,0,0,0.07)',
              borderRadius: 12,
              background: '#fff'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
                flexWrap: 'wrap'
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color: '#1f2d3d'
                }}
              >
                {heading}
                {isRequired ? <span style={{ color: '#dc3545', marginLeft: 4 }}>*</span> : null}
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '5px 9px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: '#e8f8fb',
                    border: '1px solid rgba(23,162,184,0.18)',
                    color: '#0f6f7d'
                  }}
                >
                  {renderFieldTypeLabel(type)}
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '5px 9px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: isRequired ? '#fff3cd' : '#f3f4f6',
                    border: '1px solid rgba(0,0,0,0.08)',
                    color: isRequired ? '#856404' : '#6b7280'
                  }}
                >
                  {isRequired ? 'Required' : 'Optional'}
                </span>

                {type === QUESTIONS_TEMPLATE_TYPE.ENUM ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '5px 9px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      background: '#f8fafc',
                      border: '1px solid rgba(0,0,0,0.08)',
                      color: '#475569'
                    }}
                  >
                    {enumOptions.length} options
                  </span>
                ) : null}
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 15,
                lineHeight: 1.5,
                color: '#233142'
              }}
            >
              {value !== '' && value !== null && typeof value !== 'undefined' ? String(value) : '—'}
            </div>

            {type === QUESTIONS_TEMPLATE_TYPE.ENUM && enumOptions.length > 0 ? (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: '#6b7280'
                }}
              >
                Options: {enumOptions.join(', ')}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function renderKeyValueCards(rows: Array<{ title: string; value: React.ReactNode }>): React.ReactNode {
  if (!rows.length) {
    return (
      <div
        style={{
          padding: '12px 14px',
          border: '1px solid rgba(0,0,0,0.07)',
          borderRadius: 12,
          background: '#fff',
          color: '#6b7280',
          fontSize: 14
        }}
      >
        No additional information recorded.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
      {rows.map((row, index) => (
        <div
          key={`message-additional-row-${index}`}
          style={{
            padding: '12px 14px',
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 12,
            background: '#fff'
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#8b96a3'
            }}
          >
            {row.title}
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 15,
              lineHeight: 1.45,
              color: '#233142'
            }}
          >
            {row.value || '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MessageAdditionalSummary(props: Props) {
  const { message } = props;
  const { kind } = message;

  switch (kind) {
    case MESSAGE_KIND.TournamentConfirmationRequestMessage: {
      const tournamentMessage = message as TournamentConfirmationRequestMessage;
      return <div>{renderQuestionCards(Array.isArray(tournamentMessage.fields) ? tournamentMessage.fields : [])}</div>;
    }

    case MESSAGE_KIND.EventInvitationMessage: {
      const eventMessage = message as EventInvitationMessage;
      return <div>{renderQuestionCards(Array.isArray(eventMessage.fields) ? eventMessage.fields : [])}</div>;
    }

    case MESSAGE_KIND.ClubParticipantInviteMessage: {
      const clubInviteMessage = message as ClubParticipantInviteMessage;
      return <div>{renderQuestionCards(Array.isArray(clubInviteMessage.fields) ? clubInviteMessage.fields : [])}</div>;
    }

    case MESSAGE_KIND.AbsenceMessage: {
      const absenceMessage = message as AbsenceMessage;

      const rows = AbsenceMessageRows.map(row => {
        let contentCell: React.ReactNode = '';

        switch (row.field) {
          case 'dateAbsence':
            contentCell = absenceMessage[row.field] ? moment(absenceMessage[row.field]).format(DATE_FORMAT) : '';
            break;
          case 'intervalAbsence':
            contentCell = ABSENCE_MESSAGE_INTERVAL_SERVER_TO_CLIENT_MAPPING[absenceMessage.intervalAbsence];
            break;
          case 'reasonAbsence':
            contentCell = ABSENCE_MESSAGE_REASON_SERVER_TO_CLIENT_MAPPING[absenceMessage.reasonAbsence];
            break;
          default:
            contentCell = (absenceMessage as any)[row.field];
        }

        return {
          title: row.title,
          value: contentCell || '—'
        };
      });

      return <div>{renderKeyValueCards(rows)}</div>;
    }

    case MESSAGE_KIND.EventParticipationMessage: {
      const eventParticipationMessage = message as EventParticipationMessage;

      const rows = EventParticipationMessageRows.map(row => {
        let contentCell: React.ReactNode = '';

        switch (row.field) {
          case 'isTakePart':
            contentCell = eventParticipationMessage[row.field] ? 'Yes' : 'No';
            break;
          default:
            contentCell = (eventParticipationMessage as any)[row.field];
        }

        return {
          title: row.title,
          value: contentCell || '—'
        };
      });

      return <div>{renderKeyValueCards(rows)}</div>;
    }

    default:
      return (
        <div
          style={{
            padding: '12px 14px',
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 12,
            background: '#fff',
            color: '#6b7280',
            fontSize: 14
          }}
        >
          Coming soon
        </div>
      );
  }
}


